import { Spinner, Button, Checkbox, Tabs, Tab, Input, Select, SelectItem } from "@nextui-org/react";
import React, { useEffect, useState, useMemo } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import fetchWrapper from "../../utils/fetch";
import { toast } from "react-toastify";
import debounce from 'lodash/debounce';

const defaultFilters = {
    search: '',
    is_admin: 'all',
    has_subscription: 'all',
    sort_by: 'id',
    sort_order: 'desc'
};

export default function UsersTable() {
    const [{ isAuthenticated, user, requested }] = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [anonymousUsers, setAnonymousUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [totalAnonymousUsers, setTotalAnonymousUsers] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState(new Set([]));
    const [selectedAnonymousUsers, setSelectedAnonymousUsers] = useState(new Set([]));
    const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
    const [anonymousPage, setAnonymousPage] = useState(parseInt(searchParams.get('anonymousPage') || '1'));
    const [selectedTab, setSelectedTab] = useState(searchParams.get('tab') || "normal");
    
    // Initialize filters from URL params
    const [filters, setFilters] = useState(() => ({
        search: searchParams.get('search') || defaultFilters.search,
        is_admin: searchParams.get('is_admin') || defaultFilters.is_admin,
        has_subscription: searchParams.get('has_subscription') || defaultFilters.has_subscription,
        sort_by: searchParams.get('sort_by') || defaultFilters.sort_by,
        sort_order: searchParams.get('sort_order') || defaultFilters.sort_order
    }));
    
    const [anonymousFilters, setAnonymousFilters] = useState(() => ({
        search: searchParams.get('anonymous_search') || defaultFilters.search,
        has_subscription: searchParams.get('anonymous_has_subscription') || defaultFilters.has_subscription,
        sort_by: searchParams.get('anonymous_sort_by') || defaultFilters.sort_by,
        sort_order: searchParams.get('anonymous_sort_order') || defaultFilters.sort_order
    }));

    // Add these state variables after other state declarations
    const [searchText, setSearchText] = useState(() => filters.search);
    const [anonymousSearchText, setAnonymousSearchText] = useState(() => anonymousFilters.search);

    const navigate = useNavigate();

    // Effect to update filters when URL params change
    useEffect(() => {
        const newFilters = {
            search: searchParams.get('search') || defaultFilters.search,
            is_admin: searchParams.get('is_admin') || defaultFilters.is_admin,
            has_subscription: searchParams.get('has_subscription') || defaultFilters.has_subscription,
            sort_by: searchParams.get('sort_by') || defaultFilters.sort_by,
            sort_order: searchParams.get('sort_order') || defaultFilters.sort_order
        };

        const newAnonymousFilters = {
            search: searchParams.get('anonymous_search') || defaultFilters.search,
            has_subscription: searchParams.get('anonymous_has_subscription') || defaultFilters.has_subscription,
            sort_by: searchParams.get('anonymous_sort_by') || defaultFilters.sort_by,
            sort_order: searchParams.get('anonymous_sort_order') || defaultFilters.sort_order
        };

        setFilters(newFilters);
        setAnonymousFilters(newAnonymousFilters);
        setSelectedTab(searchParams.get('tab') || "normal");
        setPage(parseInt(searchParams.get('page') || '1'));
        setAnonymousPage(parseInt(searchParams.get('anonymousPage') || '1'));
    }, [searchParams]);

    // Effect to update URL params when filters change
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        
        // Add common params
        params.set('tab', selectedTab);
        params.set('page', page.toString());
        params.set('anonymousPage', anonymousPage.toString());
        
        // Add regular user filters
        if (filters.search) {
            params.set('search', filters.search);
        } else {
            params.delete('search');
        }
        
        if (filters.is_admin !== defaultFilters.is_admin) {
            params.set('is_admin', filters.is_admin);
        } else {
            params.delete('is_admin');
        }
        
        if (filters.has_subscription !== defaultFilters.has_subscription) {
            params.set('has_subscription', filters.has_subscription);
        } else {
            params.delete('has_subscription');
        }
        
        if (filters.sort_by !== defaultFilters.sort_by) {
            params.set('sort_by', filters.sort_by);
        }
        if (filters.sort_order !== defaultFilters.sort_order) {
            params.set('sort_order', filters.sort_order);
        }
        
        // Add anonymous user filters
        if (anonymousFilters.search) {
            params.set('anonymous_search', anonymousFilters.search);
        } else {
            params.delete('anonymous_search');
        }
        
        if (anonymousFilters.has_subscription !== defaultFilters.has_subscription) {
            params.set('anonymous_has_subscription', anonymousFilters.has_subscription);
        } else {
            params.delete('anonymous_has_subscription');
        }
        
        if (anonymousFilters.sort_by !== defaultFilters.sort_by) {
            params.set('anonymous_sort_by', anonymousFilters.sort_by);
        }
        if (anonymousFilters.sort_order !== defaultFilters.sort_order) {
            params.set('anonymous_sort_order', anonymousFilters.sort_order);
        }
        
        setSearchParams(params, { replace: true });
    }, [filters, anonymousFilters, selectedTab, page, anonymousPage]);

    const loadUsers = async (isAnonymous = false) => {
        setIsLoading(true);
        try {
            if (isAnonymous) {
                const anonymousData = await fetchWrapper.get(`/admin/anonymous-users?${new URLSearchParams({
                    page: anonymousPage,
                    search: anonymousFilters.search || '',
                    has_subscription: anonymousFilters.has_subscription === 'true' ? true : (anonymousFilters.has_subscription === 'false' ? false : ''),
                    sort_by: anonymousFilters.sort_by,
                    sort_order: anonymousFilters.sort_order,
                    is_laravel: true
                }).toString()}`, true);
                setAnonymousUsers(anonymousData.users.data);
                setTotalAnonymousUsers(anonymousData.total);
            } else {
                const usersData = await fetchWrapper.get(`/admin/users?${new URLSearchParams({
                    page,
                    search: filters.search || '',
                    is_admin: filters.is_admin === 'true' ? true : (filters.is_admin === 'false' ? false : ''),
                    has_subscription: filters.has_subscription === 'true' ? true : (filters.has_subscription === 'false' ? false : ''),
                    sort_by: filters.sort_by,
                    sort_order: filters.sort_order,
                    is_laravel: true
                }).toString()}`, true);
                setUsers(usersData.users.data);
                setTotalUsers(usersData.total);
            }
        } catch (err) {
            toast.error(err?.message || "خطا در بارگذاری کاربران");
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedLoadUsers = useMemo(
        () => debounce(loadUsers, 300),
        [page, anonymousPage, filters, anonymousFilters]
    );

    // Load initial data based on selected tab
    useEffect(() => {
        loadUsers(selectedTab === 'anonymous');
    }, []);

    // Load data when filters change
    useEffect(() => {
        if (selectedTab === 'anonymous') {
            debouncedLoadUsers(true);
        } else {
            debouncedLoadUsers(false);
        }
        return () => debouncedLoadUsers.cancel();
    }, [page, anonymousPage, filters, anonymousFilters, selectedTab]);

    const handleTabChange = (key) => {
        setSelectedTab(key);
        // Reset page when changing tabs
        if (key === "normal") {
            setPage(1);
            loadUsers(false);
        } else {
            setAnonymousPage(1);
            loadUsers(true);
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            await fetchWrapper.delete(`/admin/users/${userId}?is_laravel=true`, true);
            toast.success("کاربر با موفقیت حذف شد");
            loadUsers(false);
        } catch (err) {
            toast.error(err?.message || "خطا در حذف کاربر");
        }
    };

    const handleDeleteSelectedUsers = async () => {
        try {
            await fetchWrapper.post('/admin/users/delete-multiple', {
                user_ids: Array.from(selectedUsers),
                is_laravel: true
            }, true);
            toast.success("کاربران انتخاب شده با موفقیت حذف شدند");
            setSelectedUsers(new Set([]));
            loadUsers(false);
        } catch (err) {
            toast.error(err?.message || "خطا در حذف کاربران");
        }
    };

    const handleDeleteAnonymousUser = async (userId) => {
        try {
            await fetchWrapper.delete(`/admin/anonymous-users/${userId}?is_laravel=true`, true);
            toast.success("کاربر ناشناس با موفقیت حذف شد");
            loadUsers(true);
        } catch (err) {
            toast.error(err?.message || "خطا در حذف کاربر ناشناس");
        }
    };

    const handleDeleteSelectedAnonymousUsers = async () => {
        try {
            await fetchWrapper.post('/admin/anonymous-users/delete-multiple', {
                user_ids: Array.from(selectedAnonymousUsers),
                is_laravel: true
            }, true);
            toast.success("کاربران ناشناس انتخاب شده با موفقیت حذف شدند");
            setSelectedAnonymousUsers(new Set([]));
            loadUsers(true);
        } catch (err) {
            toast.error(err?.message || "خطا در حذف کاربران ناشناس");
        }
    };

    const prevPage = () => setPage(page => page - 1);
    const nextPage = () => setPage(page => page + 1);
    const prevAnonymousPage = () => setAnonymousPage(page => page - 1);
    const nextAnonymousPage = () => setAnonymousPage(page => page + 1);

    const getInterval = (datetime) => {
        if (!datetime) return '';
        const now = new Date();
        const diff = new Date(datetime) - now;
        const formatter = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
        return formatter.format(Math.round(diff / 86400000), "day");
    };

    const getRelativeTime = (datetime) => {
        if (!datetime) return '';
        const now = new Date();
        const date = new Date(datetime);
        const diffInSeconds = Math.floor((now - date) / 1000);

        const formatter = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
        
        if (diffInSeconds < 60) {
            return formatter.format(-diffInSeconds, "second");
        } else if (diffInSeconds < 3600) {
            return formatter.format(-Math.floor(diffInSeconds / 60), "minute");
        } else if (diffInSeconds < 86400) {
            return formatter.format(-Math.floor(diffInSeconds / 3600), "hour");
        } else if (diffInSeconds < 2592000) {
            return formatter.format(-Math.floor(diffInSeconds / 86400), "day");
        } else if (diffInSeconds < 31536000) {
            return formatter.format(-Math.floor(diffInSeconds / 2592000), "month");
        } else {
            return formatter.format(-Math.floor(diffInSeconds / 31536000), "year");
        }
    };

    const formatDateTime = (datetime) => {
        if (!datetime) return '';
        const date = new Date(datetime);
        return new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).format(date);
    };

    // Add this function before renderFilters
    const handleSearch = (isAnonymous = false) => {
        if (isAnonymous) {
            setAnonymousFilters(prev => ({
                ...prev,
                search: anonymousSearchText
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                search: searchText
            }));
        }
    };

    const handleKeyPress = (e, isAnonymous = false) => {
        if (e.key === 'Enter') {
            handleSearch(isAnonymous);
        }
    };

    const renderFilters = (isAnonymous = false) => {
        const currentFilters = isAnonymous ? anonymousFilters : filters;
        const setCurrentFilters = isAnonymous ? setAnonymousFilters : setFilters;
        const currentSearchText = isAnonymous ? anonymousSearchText : searchText;
        const setCurrentSearchText = isAnonymous ? setAnonymousSearchText : setSearchText;

        return (
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="flex gap-2">
                    <Input
                        type="text"
                        label="جستجو"
                        placeholder={isAnonymous ? "جستجو در نام کاربری" : "جستجو در نام، نام کاربری یا ایمیل"}
                        value={currentSearchText}
                        onChange={(e) => setCurrentSearchText(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, isAnonymous)}
                        endContent={
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onClick={() => handleSearch(isAnonymous)}
                                className="text-default-400 hover:text-default-600"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                </svg>
                            </Button>
                        }
                    />
                </div>
                
                {!isAnonymous && (
                    <Select
                        label="وضعیت ادمین"
                        selectedKeys={[currentFilters.is_admin]}
                        value={currentFilters.is_admin}
                        onChange={(e) => setCurrentFilters({
                            ...currentFilters,
                            is_admin: e.target.value
                        })}
                        items={[
                            { key: "all", label: "همه" },
                            { key: "true", label: "ادمین" },
                            { key: "false", label: "کاربر عادی" }
                        ]}
                    >
                        {(item) => (
                            <SelectItem key={item.key} value={item.key}>
                                {item.label}
                            </SelectItem>
                        )}
                    </Select>
                )}

                <Select
                    label="وضعیت اشتراک"
                    selectedKeys={[currentFilters.has_subscription]}
                    value={currentFilters.has_subscription}
                    onChange={(e) => setCurrentFilters({
                        ...currentFilters,
                        has_subscription: e.target.value
                    })}
                    items={[
                        { key: "all", label: "همه" },
                        { key: "true", label: "دارای اشتراک" },
                        { key: "false", label: "بدون اشتراک" }
                    ]}
                >
                    {(item) => (
                        <SelectItem key={item.key} value={item.key}>
                            {item.label}
                        </SelectItem>
                    )}
                </Select>

                <Select
                    label="مرتب‌سازی"
                    selectedKeys={[`${currentFilters.sort_by}-${currentFilters.sort_order}`]}
                    value={`${currentFilters.sort_by}-${currentFilters.sort_order}`}
                    onChange={(e) => {
                        const [sort_by, sort_order] = e.target.value.split('-');
                        setCurrentFilters({
                            ...currentFilters,
                            sort_by,
                            sort_order
                        });
                    }}
                    items={[
                        { key: "id-desc", label: "جدیدترین" },
                        { key: "id-asc", label: "قدیمی‌ترین" },
                        { key: "username-asc", label: "نام کاربری (صعودی)" },
                        { key: "username-desc", label: "نام کاربری (نزولی)" },
                        ...(!isAnonymous ? [
                            { key: "email-asc", label: "ایمیل (صعودی)" },
                            { key: "email-desc", label: "ایمیل (نزولی)" }
                        ] : [])
                    ]}
                >
                    {(item) => (
                        <SelectItem key={item.key} value={item.key}>
                            {item.label}
                        </SelectItem>
                    )}
                </Select>
            </div>
        );
    };

    const renderPagination = (currentPage, prevHandler, nextHandler, total) => {
        const totalPages = Math.ceil(total / 30); // 30 is the per_page value
        
        return (
            <nav className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between items-center">
                    <div className="text-sm text-gray-700">
                        صفحه {currentPage} از {totalPages} ({total} مورد)
                    </div>
                    <div className="flex justify-end space-x-2 space-x-reverse">
                        <Button
                            onClick={prevHandler}
                            isDisabled={currentPage === 1}
                            variant="bordered"
                            className="text-default-600"
                        >
                            قبلی
                        </Button>
                        <Button
                            onClick={nextHandler}
                            isDisabled={currentPage >= totalPages}
                            variant="bordered"
                            className="mr-3 text-default-600"
                        >
                            بعدی
                        </Button>
                    </div>
                </div>
            </nav>
        );
    };

    const renderTable = (data, columns, isAnonymousTable = false) => {
        const selectedSet = isAnonymousTable ? selectedAnonymousUsers : selectedUsers;
        const setSelectedSet = isAnonymousTable ? setSelectedAnonymousUsers : setSelectedUsers;
        const handleDelete = isAnonymousTable ? handleDeleteAnonymousUser : handleDeleteUser;
        const handleDeleteSelected = isAnonymousTable ? handleDeleteSelectedAnonymousUsers : handleDeleteSelectedUsers;

        return (
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-right text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th scope="col" className="p-4">
                                <Checkbox
                                    isSelected={data.length > 0 && selectedSet.size === data.length}
                                    isIndeterminate={selectedSet.size > 0 && selectedSet.size < data.length}
                                    onValueChange={(checked) => {
                                        if (checked) {
                                            setSelectedSet(new Set(data.map(item => item.id)));
                                        } else {
                                            setSelectedSet(new Set([]));
                                        }
                                    }}
                                />
                            </th>
                            {columns.map((column, index) => (
                                <th key={index} scope="col" className="px-6 py-3">
                                    {column.header}
                                </th>
                            ))}
                            <th scope="col" className="px-6 py-3">
                                عملیات
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, rowIndex) => (
                            <tr key={rowIndex} className={`border-b hover:bg-gray-50 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <td className="w-4 p-4">
                                    <Checkbox
                                        isSelected={selectedSet.has(item.id)}
                                        onValueChange={(checked) => {
                                            const newSelected = new Set(selectedSet);
                                            if (checked) {
                                                newSelected.add(item.id);
                                            } else {
                                                newSelected.delete(item.id);
                                            }
                                            setSelectedSet(newSelected);
                                        }}
                                    />
                                </td>
                                {columns.map((column, colIndex) => (
                                    <td key={colIndex} className="px-6 py-4">
                                        {column.render(item)}
                                    </td>
                                ))}
                                <td className="px-6 py-4">
                                    <Button
                                        color="danger"
                                        variant="light"
                                        isIconOnly
                                        onClick={() => handleDelete(item.id)}
                                        className="text-red-600 hover:text-red-900"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                        </svg>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {selectedSet.size > 0 && (
                    <div className="absolute bottom-4 left-4">
                        <Button
                            color="danger"
                            variant="solid"
                            onClick={handleDeleteSelected}
                            className="flex items-center gap-2"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                            حذف {selectedSet.size} کاربر انتخاب شده
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const normalColumns = [
        { key: 'id', header: 'شناسه', render: (item) => item.id },
        { key: 'username', header: 'نام کاربری', render: (item) => item.username },
        { key: 'first_name', header: 'نام', render: (item) => item.first_name },
        { key: 'last_name', header: 'نام خانوادگی', render: (item) => item.last_name },
        { key: 'email', header: 'ایمیل', render: (item) => item.email },
        { key: 'is_admin', header: 'ادمین؟', render: (item) => item.is_admin ? 'بله' : 'خیر' },
        { 
            key: 'active_subscription', 
            header: 'عضویت ویژه تا', 
            render: (item) => item.active_subscription ? getInterval(item.active_subscription.valid_until) : 'ندارد'
        },
        {
            key: 'created_at',
            header: 'تاریخ ثبت‌نام',
            render: (item) => (
                <div className="tooltip-container relative group">
                    <span>{getRelativeTime(item.created_at)}</span>
                    <div className="tooltip absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                        {formatDateTime(item.created_at)}
                    </div>
                </div>
            )
        }
    ];

    const anonymousColumns = [
        { key: 'id', header: 'شناسه', render: (item) => item.id },
        { key: 'username', header: 'نام کاربری', render: (item) => item.username },
        { key: 'ip_address', header: 'آدرس IP', render: (item) => item.ip_address || 'نامشخص' },
        { 
            key: 'active_subscription', 
            header: 'عضویت ویژه تا', 
            render: (item) => item.active_subscription ? getInterval(item.active_subscription.valid_until) : 'ندارد'
        },
        {
            key: 'created_at',
            header: 'تاریخ ثبت‌نام',
            render: (item) => (
                <div className="tooltip-container relative group">
                    <span>{getRelativeTime(item.created_at)}</span>
                    <div className="tooltip absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                        {formatDateTime(item.created_at)}
                    </div>
                </div>
            )
        }
    ];

    if (requested) {
        if (isAuthenticated) {
            return (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 my-16">
                    <div className="sm:flex sm:items-center mb-8">
                        <div className="sm:flex-auto text-center">
                            <h1 className="text-xl font-semibold text-default-900">مدیریت کاربران</h1>
                            <p className="mt-2 text-sm text-default-500">
                                در این بخش می‌توانید کاربران عادی و ناشناس را مدیریت کنید
                            </p>
                        </div>
                    </div>

                    <Tabs 
                        selectedKey={selectedTab}
                        onSelectionChange={handleTabChange}
                        aria-label="User Management Tabs"
                        className="mb-8"
                        variant="underlined"
                        color="primary"
                    >
                        <Tab 
                            key="normal" 
                            title={
                                <div className="flex items-center space-x-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 ml-2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                    </svg>
                                    کاربران عادی
                                </div>
                            }
                        >
                            {renderFilters(false)}
                            {isLoading ? (
                                <div className="flex items-center justify-center h-64">
                                    <Spinner size="lg"/>
                                </div>
                            ) : (
                                <>
                                    {renderTable(users, normalColumns)}
                                    {renderPagination(page, prevPage, nextPage, totalUsers)}
                                </>
                            )}
                        </Tab>
                        <Tab 
                            key="anonymous" 
                            title={
                                <div className="flex items-center space-x-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 ml-2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                    </svg>
                                    کاربران ناشناس
                                </div>
                            }
                        >
                            {renderFilters(true)}
                            {isLoading ? (
                                <div className="flex items-center justify-center h-64">
                                    <Spinner size="lg"/>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        {selectedAnonymousUsers.size > 0 && (
                                            <Button
                                                color="danger"
                                                variant="flat"
                                                onClick={handleDeleteSelectedAnonymousUsers}
                                                className="flex items-center space-x-2"
                                                startContent={
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                                    </svg>
                                                }
                                            >
                                                حذف {selectedAnonymousUsers.size} کاربر انتخاب شده
                                            </Button>
                                        )}
                                    </div>
                                    {renderTable(anonymousUsers, anonymousColumns, true)}
                                    {renderPagination(anonymousPage, prevAnonymousPage, nextAnonymousPage, totalAnonymousUsers)}
                                </>
                            )}
                        </Tab>
                    </Tabs>
                </div>
            );
        } else {
            return <Navigate to="/login"/>;
        }
    } else {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner size="lg"/>
            </div>
        );
    }
}
